import { Component, OnInit } from '@angular/core';
import { JsonPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminActionResponse, AdminDailyVisitsMetric, AdminExtractionTestRequest, AdminStatus, AdminUser } from '../../core/models';
import { ApiService } from '../../core/api.service';
import { SessionService } from '../../core/session.service';
import { PageSection } from '../page-section/page-section';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';

@Component({ selector: 'app-admin-panel', standalone: true, imports: [FormsModule, JsonPipe, PageSection, LoadingSpinner], templateUrl: './admin-panel.html', styleUrl: './admin-panel.scss' })
export class AdminPanel implements OnInit {
  status: AdminStatus | null = null;
  adminUsers: AdminUser[] = [];
  userSearchTerm = '';
  userStatusFilter = 'all';
  userGroupFilter: 'all' | 'admin' | 'modo' | 'not_promoted' = 'all';
  nextUsersToken = '';
  lastResult: AdminActionResponse | Record<string, unknown> | null = null;
  error = '';
  isLoading = false;
  isLoadingStatus = true;
  isLoadingUsers = true;
  selectedSource = 'all';
  selectedMatchingAction = 'plan_matching';
  testNotificationChannel: 'email' | 'whatsapp' = 'email';
  testRecipient = '';
  testNotificationName = '';
  extractionDraft: AdminExtractionTestRequest = {
    title: '',
    organization: '',
    location: '',
    sourceUrl: '',
    description: ''
  };
  activeVisitPoint: { point: AdminDailyVisitsMetric; index: number; x: number } | null = null;
  visitCursorGuideX: number | null = null;

  readonly fallbackSources = ['all', 'euraxess', 'abg', 'inria_jobs', 'cnrs_emploi', 'academic_positions'];
  readonly fallbackMatchingActions = ['daily_ingestion', 'plan_matching', 'sanitize_offers', 'ai_batch_controller', 'plan_notifications'];
  readonly matchingActionLabels: Record<string, string> = {
    daily_ingestion: 'Run daily ingestion',
    plan_matching: 'Plan offer matching',
    sanitize_offers: 'Clean offer data',
    ai_batch_controller: 'Run AI matching batches',
    plan_notifications: 'Plan match notifications'
  };

  constructor(private api: ApiService, public session: SessionService) {}

  ngOnInit(): void { void this.initializePanel(); }

  get crawlSources(): string[] { return this.status?.actions?.crawlSources?.length ? this.status.actions.crawlSources : this.fallbackSources; }
  get matchingActions(): string[] { return this.status?.actions?.matchingActions?.length ? this.status.actions.matchingActions : this.fallbackMatchingActions; }
  get availableUserStatuses(): string[] {
    return Array.from(new Set(this.adminUsers.map(user => this.userStatusLabel(user)).filter(Boolean))).sort((left, right) => left.localeCompare(right));
  }
  get visitTrend(): AdminDailyVisitsMetric[] { return this.status?.metrics?.totalVisits?.daily || []; }
  get visitTrendMax(): number { return Math.max(1, ...this.visitTrend.map(point => point.count || 0)); }
  get visitTrendTotal(): number { return this.visitTrend.reduce((total, point) => total + (point.count || 0), 0); }
  get visitTooltipLeftPercent(): number { return this.activeVisitPoint ? this.activeVisitPoint.x : 50; }
  get visitCursorGuideLeftPercent(): number { return this.visitCursorGuideX ?? 0; }
  get visitTrendPath(): string {
    const points = this.visitTrend;
    if (!points.length) return '';
    const max = this.visitTrendMax;
    return points.map((point, index) => {
      const x = this.visitPointX(index);
      const y = 98 - ((point.count || 0) / max) * 88;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ');
  }
  get filteredAdminUsers(): AdminUser[] {
    const term = this.userSearchTerm.trim().toLowerCase();
    return this.adminUsers.filter(user => {
      const status = this.userStatusLabel(user);
      const matchesStatus = this.userStatusFilter === 'all' || status === this.userStatusFilter;
      const matchesGroup = this.userGroupFilter === 'all'
        || (this.userGroupFilter === 'admin' && user.isAdmin)
        || (this.userGroupFilter === 'modo' && !user.isAdmin && user.isModerator)
        || (this.userGroupFilter === 'not_promoted' && !user.isAdmin && !user.isModerator);
      const searchable = [user.firstName, user.lastName, user.email, user.username, status, ...user.groups].join(' ').toLowerCase();
      return matchesStatus && matchesGroup && (!term || searchable.includes(term));
    });
  }

  matchingActionLabel(action: string): string { return this.matchingActionLabels[action] || action.replace(/_/g, ' '); }
  userDisplayName(user: AdminUser): string { return [user.firstName, user.lastName].map(part => part?.trim()).filter(Boolean).join(' ') || user.email || user.username; }
  userStatusLabel(user: AdminUser): string { return user.status || (user.enabled ? 'ENABLED' : 'DISABLED'); }
  visitBarHeight(point: AdminDailyVisitsMetric): number { return Math.max(4, ((point.count || 0) / this.visitTrendMax) * 100); }
  visitPointX(index: number): number { return this.visitTrend.length ? ((index + .5) / this.visitTrend.length) * 100 : 50; }
  visitPointY(point: AdminDailyVisitsMetric): number { return 98 - ((point.count || 0) / this.visitTrendMax) * 88; }
  showVisitPointInfo(point: AdminDailyVisitsMetric, index: number): void {
    const x = this.visitPointX(index);
    this.visitCursorGuideX = x;
    this.activeVisitPoint = { point, index, x };
  }

  updateVisitCursorGuide(event: PointerEvent): void {
    const target = event.currentTarget as HTMLElement | null;
    if (!target || !this.visitTrend.length) return;

    const rect = target.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
    const closestIndex = this.closestVisitPointIndex(relativeX);
    this.showVisitPointInfo(this.visitTrend[closestIndex], closestIndex);
  }

  hideVisitCursorGuide(): void {
    this.visitCursorGuideX = null;
    this.activeVisitPoint = null;
  }

  visitDateLabel(value: string): string {
    if (!value) return '';
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  async refreshStatus(): Promise<void> {
    if (this.isLoadingStatus) return;
    this.isLoadingStatus = true;
    this.error = '';
    try {
      this.status = await this.api.getAdminStatus();
      this.hideVisitCursorGuide();
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Admin status could not be loaded.';
    } finally {
      this.isLoadingStatus = false;
    }
  }
  async loadUsers(nextToken = ''): Promise<void> {
    if (this.isLoadingUsers) return;
    this.isLoadingUsers = true;
    this.error = '';
    try {
      const result = await this.api.listAdminUsers(25, nextToken);
      this.adminUsers = nextToken ? [...this.adminUsers, ...result.items] : result.items;
      this.nextUsersToken = result.nextToken || '';
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Admin users could not be loaded.';
    } finally {
      this.isLoadingUsers = false;
    }
  }
  async updateUserRole(user: AdminUser, group: 'ADMIN' | 'MODO', action: 'promote' | 'demote'): Promise<void> {
    await this.run(() => this.api.updateAdminUserRole(user.username, group, action), result => {
      this.adminUsers = this.adminUsers.map(item => item.username === result.user.username ? result.user : item);
      this.lastResult = result as unknown as Record<string, unknown>;
    }, false);
  }
  async setUserRole(user: AdminUser, group: 'ADMIN' | 'MODO', enabled: boolean): Promise<void> {
    const currentlyEnabled = group === 'ADMIN' ? user.isAdmin : user.groups.includes('MODO');
    if (currentlyEnabled === enabled) return;
    await this.updateUserRole(user, group, enabled ? 'promote' : 'demote');
  }
  async forceCrawl(): Promise<void> { await this.run(() => this.api.forceAdminCrawl(this.selectedSource)); }
  async runMatching(): Promise<void> { await this.run(() => this.api.runAdminMatching(this.selectedMatchingAction)); }
  async testApi(): Promise<void> { await this.run(() => this.api.testAdminApi()); }
  async sendTestNotification(): Promise<void> {
    await this.run(() => this.api.sendAdminTestNotification({
      recipient: this.testRecipient || this.session.currentUser()?.email || '',
      channel: this.testNotificationChannel,
      preferredLanguage: this.session.currentUser()?.preferredLanguage || 'en',
      name: this.testNotificationName || this.session.currentUser()?.email || 'there'
    }));
  }
  async testExtraction(): Promise<void> { await this.run(() => this.api.testAdminExtraction(this.normalizedExtractionDraft())); }
  async extractExistingOffers(): Promise<void> { await this.run(() => this.api.extractExistingOffers()); }

  private async initializePanel(): Promise<void> {
    this.isLoadingStatus = false;
    this.isLoadingUsers = false;
    await Promise.all([this.refreshStatus(), this.loadUsers()]);
  }

  private normalizedExtractionDraft(): AdminExtractionTestRequest {
    return {
      title: this.extractionDraft.title.trim(),
      organization: this.extractionDraft.organization?.trim(),
      location: this.extractionDraft.location?.trim(),
      sourceUrl: this.extractionDraft.sourceUrl?.trim(),
      description: this.extractionDraft.description.trim()
    };
  }

  private closestVisitPointIndex(x: number): number {
    if (!this.visitTrend.length) return 0;
    return this.visitTrend.reduce((closestIndex, _point, index) => {
      const currentDistance = Math.abs(this.visitPointX(index) - x);
      const closestDistance = Math.abs(this.visitPointX(closestIndex) - x);
      return currentDistance < closestDistance ? index : closestIndex;
    }, 0);
  }

  private async run<T extends AdminActionResponse | AdminStatus | { items: AdminUser[]; nextToken?: string } | Record<string, unknown>>(action: () => Promise<T>, onSuccess?: (result: T) => void, setLastResult = true): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;
    this.error = '';
    try {
      const result = await action();
      onSuccess?.(result);
      if (setLastResult && 'status' in result) this.lastResult = result as AdminActionResponse;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Admin action failed.';
    } finally {
      this.isLoading = false;
    }
  }
}
